import { Controller } from 'stimulus'
import './mixins/dataConfirmModal'
import './mixins/jQueryExtentions'

export default class extends Controller {
  connect() {
    // TODO - Deprecation: We should avoid assigning the Stimullus controller
    // directly in the element properties. This is kept here for backwards compatibility.
    this.element[this.identifier] = this
    // The better approach is to hold an element's attached Stimulus controllers
    // in its own object for better namespacing.
    this.element.controllers ||= {}
    this.element.controllers[this.identifier] = this

    this._controllerPrefix = `data-${this.identifier}`
    this.doConnect()
    this.connected = true
    Promise.resolve().then(() => { this.afterConnect() })
  }

  doConnect() {
    throw new Error(`You have to implement the method doConnect instead of the connect method on the ${this.identifier} stimulus controller!`)
  }

  // Overwrite method and use to execute tasks that require all the stimulus pieces
  // (controllers, targets, values, etc..) on the page to be connected
  afterConnect() {}

  disconnect() {
    this.doDisconnect()
    this.connected = false
  }

  doDisconnect() {
    // TODO: Commenting out throwing the Error below. It's floading the JS console with errors.
    // We need another PR to refactor and update all the Stimulus controllers to use doDisconnect() instead of disconnect()
    // throw new Error(`You have to implement the method doDisconnect instead of the disconnect method on the ${this.identifier} stimulus controller!`)
  }

  isActionDisabled(action) {
    if (this.element.hasAttribute(this.attributeToDisable)) {
      const disabledActions = this.disabledActions
      if (disabledActions.size > 0) { return disabledActions.has(action) }

      return true
    }

    return false
  }

  // Validate if the event was triggered by the same element the event listener is attached to.
  // Returns true if the attribute with the actions that should be validated
  //    wasn't defined.
  // Note: It will search for the attribute in the element that has the stimulus-action attached.
  validateEventTriggeredByCurrentTarget(event, action) {
    const actionsTriggeredOnlyByCurrentTargetAttributeName = `${this._controllerPrefix}-actions-triggered-only-by-current-target`
    if (event.currentTarget != window) {
      if (this.hasWordInAttributeValue(event.currentTarget, actionsTriggeredOnlyByCurrentTargetAttributeName, action)) {
        return event.currentTarget == event.target
      }
    }

    return true
  }

  // Checks if a DOM element has a has a word(string without spaces) inside an attribute's value.
  // Returns false if the element doesnt have the attribute passed.
  hasWordInAttributeValue(element, attribute, wordToCheck) {
    if (element.hasAttribute(attribute)) {
      const attrWords = new Set((element.getAttribute(attribute) || '').split(' '))
      return attrWords.has(wordToCheck)
    }
    return false
  }

  // Search a DOM element in an element DOM list that has a word(string without spaces) inside an attribute's value
  searchElementWithWordInAttribute(elementsList, attribute, word) {
    let targetFound = null
    for (let index = 0; index < elementsList.length; index++) {
      if (this.hasWordInAttributeValue(elementsList[index], attribute, word)) {
        targetFound = elementsList[index]
        break
      }
    }
    return targetFound
  }

  // Search for a stimulus target inside an element
  getTargetIn(element, targetName, controller = this.identifier) {
    let targetFound = null
    const possibleStimulusTargets = element.querySelectorAll(`[data-${controller}-target]`)
    targetFound = this.searchElementWithWordInAttribute(
      possibleStimulusTargets,
      `data-${controller}-target`,
      targetName
    )

    // Check for deprecated stimulus targets
    // (this will be able to be deleted after full upgrade to new stimulus target sintax)
    if (!targetFound) {
      const possibleStimulusDeprecatedTargets = element.querySelectorAll('[data-target]')
      targetFound = this.searchElementWithWordInAttribute(
        possibleStimulusDeprecatedTargets,
        'data-target',
        `${controller}.${targetName}`
      )
    }

    return targetFound
  }

  get disabledActions() {
    return new Set((this.data.get('actions-to-disable') || '').split(' '))
  }

  get attributeToDisable() {
    return this.data.get('attribute-to-disable')
  }

  getParamFullName(param) {
    return `${this._controllerPrefix}-param-${param}`
  }

  hasParam(element, param) {
    return element.hasAttribute(this.getParamFullName(param))
  }

  getParam(element, param) {
    return element.getAttribute(this.getParamFullName(param))
  }

  setParam(element, param, value) {
    element.setAttribute(this.getParamFullName(param), value)
  }

  isPresent(value) {
    return value != undefined && value != ''
  }

  dispatchCustomEvent(eventName, { element = this.element, detail = {}, scoped = true, bubbles = true }) {
    if (scoped) {
      eventName = `${this.identifier}-${eventName}`
    }

    const event = new CustomEvent(eventName, { bubbles, detail })
    element.dispatchEvent(event)
  }

  detectKeyStrokes(event, callback) {
    const key = event.keyCode || event.code

    if (key === 32 && event.target !== document.body) {
      event.preventDefault()
    }

    if (key === null || key === undefined || key === 13 || key === 32) {
      callback()
    }
  }
}
